import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, KeyRound, CheckCircle } from "lucide-react";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);

  useEffect(() => {
    // Listen for PASSWORD_RECOVERY event from the email link
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsRecovery(true);
      }
    });

    // Also check the URL hash for type=recovery (handles page reload)
    const hash = window.location.hash;
    if (hash.includes("type=recovery")) {
      setIsRecovery(true);
    }

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError(t("resetPassword.tooShort", "Password must be at least 6 characters."));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("resetPassword.mismatch", "Passwords do not match."));
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setSuccess(true);
      setTimeout(() => navigate("/home"), 2000);
    } catch (err: any) {
      setError(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  if (!isRecovery) {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm bg-gradient-card border border-border rounded-2xl p-6 shadow-card text-center space-y-4">
          <KeyRound className="w-10 h-10 text-primary mx-auto" />
          <h2 className="font-display text-xl font-bold text-foreground">{t("resetPassword.title", "Reset Password")}</h2>
          <p className="text-sm text-muted-foreground">{t("resetPassword.invalidLink", "This link is invalid or has expired. Please request a new password reset.")}</p>
          <button onClick={() => navigate("/auth")} className="text-sm text-primary font-medium">
            {t("resetPassword.backToSignIn", "Back to Sign In")}
          </button>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm bg-gradient-card border border-border rounded-2xl p-6 shadow-card text-center space-y-4">
          <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />
          <h2 className="font-display text-xl font-bold text-foreground">{t("resetPassword.successTitle", "Password Updated")}</h2>
          <p className="text-sm text-muted-foreground">{t("resetPassword.successMessage", "Your password has been changed. Redirecting…")}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm bg-gradient-card border border-border rounded-2xl p-6 shadow-card space-y-6">
        <div className="text-center">
          <KeyRound className="w-10 h-10 text-primary mx-auto mb-2" />
          <h2 className="font-display text-2xl font-bold text-foreground">{t("resetPassword.title", "Reset Password")}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t("resetPassword.subtitle", "Enter your new password below.")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("resetPassword.newPassword", "New Password")}</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="w-full px-4 py-3 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="••••••••" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">{t("resetPassword.confirmPassword", "Confirm Password")}</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className="w-full px-4 py-3 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" placeholder="••••••••" />
          </div>

          {error && <p className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>}

          <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("resetPassword.updateButton", "Update Password")}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
